import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getUserLoginHistory, getUser } from "../../../data";
import { AdminLayout } from "../shared/layout";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Clock, Globe, Monitor, Smartphone } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

export function UserLoginHistoryScreen() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: userData, isLoading: isUserLoading } = useQuery({
    queryKey: ["user", id],
    queryFn: () => getUser(id),
  });

  const { data: historyData, isLoading: isHistoryLoading } = useQuery({
    queryKey: ["user-login-history", id],
    queryFn: () => getUserLoginHistory(id),
  });

  const isLoading = isUserLoading || isHistoryLoading;
  const user = userData?.data;
  const currentLogin = historyData?.data?.currentLogin;
  const history = historyData?.data?.history || [];

  const getPlatformIcon = (platform) => {
    if (!platform) return <Globe className="h-4 w-4" />;
    
    const platformLower = platform.toLowerCase();
    if (platformLower.includes("mobile") || platformLower.includes("android") || platformLower.includes("ios")) {
      return <Smartphone className="h-4 w-4 text-blue-500" />;
    }
    return <Monitor className="h-4 w-4 text-green-500" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd MMM yyyy, HH:mm", { locale: tr });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <AdminLayout
      title="Login Geçmişi"
      description={`${user?.name || user?.email} kullanıcısının giriş geçmişi`}
      loading={isLoading}
    >
      <div className="space-y-6">
        <Button
          variant="outline"
          onClick={() => navigate("/admin/users")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Kullanıcılara Dön
        </Button>

        {/* User Info Card */}
        {user && (
          <Card>
            <CardHeader>
              <CardTitle>Kullanıcı Bilgileri</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">İsim</p>
                  <p className="font-medium">{user.name || "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">E-posta</p>
                  <p className="font-medium">{user.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Durum</p>
                  <Badge variant={user.status === "ACTIVE" ? "success" : "secondary"}>
                    {user.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Kayıt Tarihi</p>
                  <p className="font-medium">{formatDate(user.createdAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Login Info */}
        {currentLogin && currentLogin.lastLoginAt && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="mr-2 h-5 w-5" />
                Son Giriş Bilgisi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Tarih</p>
                  <p className="font-medium">{formatDate(currentLogin.lastLoginAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">IP Adresi</p>
                  <p className="font-medium">{currentLogin.lastLoginIp || "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 flex items-center">
                    Platform
                  </p>
                  <div className="flex items-center">
                    {getPlatformIcon(currentLogin.lastLoginPlatform)}
                    <span className="ml-2 font-medium">
                      {currentLogin.lastLoginPlatform || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Login History Table */}
        <Card>
          <CardHeader>
            <CardTitle>Giriş Geçmişi ({history.length} kayıt)</CardTitle>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Henüz giriş geçmişi bulunmuyor.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tarih</TableHead>
                    <TableHead>IP Adresi</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>User Agent</TableHead>
                    <TableHead>İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <div className="flex items-center">
                          <Clock className="mr-2 h-4 w-4 text-gray-400" />
                          {formatDate(entry.createdAt)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {entry.ipAddress || "N/A"}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          {getPlatformIcon(entry.platform)}
                          <span className="ml-2">{entry.platform || "N/A"}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs truncate text-sm text-gray-600" title={entry.userAgent}>
                          {entry.userAgent || "N/A"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{entry.action}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

